import { useResponsive } from '@/contexts/ResponsiveContext';
import { getFailedQuestions, getUnansweredQuestions } from '@/utils/managerAuditData';
import { moveScrollIntoView } from '@/utils/utils';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const getBookmarkId = (item) => (
  `${item?.bookmarkId || 'section'}-${item?._id}`
);

export const moveToBookmark = (item) => {
  moveScrollIntoView(getBookmarkId(item));
};

const BookmarkContext = createContext({
  selected: null,
  updateSelected: ({ _id, bookmarkId }) => (_id, bookmarkId),
  moveToSelected: (val) => val,
  scrollInTable: null
});

const useBookmarkContext = () => {
  const context = useContext(BookmarkContext);
  if (context === undefined) {
    throw new Error('useBookmarkContext must be used within a BookmarkProvider');
  }

  return context;
};

const BookmarkProvider = ({ scrollInTable, ...props }) => {
  const [selected, setSelected] = useState(null);

  const updateSelected = useCallback(selected => {
    setSelected(selected);
  }, []);

  const moveToSelected = useCallback(() => {
    moveToBookmark(selected);
  }, [selected]);

  return (
    <BookmarkContext.Provider
      value={ {
        selected,
        updateSelected,
        moveToSelected,
        scrollInTable
      } }
      { ...props }
    />
  );
};

const useMoveSelectedBookmark = () => {
  const { isTablet } = useResponsive();
  const { selected, moveToSelected, scrollInTable } = useBookmarkContext();

  useEffect(() => {
    const handleResize = () => { };

    window.addEventListener('resize', handleResize);

    if ((selected)) {
      if (scrollInTable || !isTablet) {
        moveToSelected?.();
      }
      else {
        if (!scrollInTable)
          moveScrollIntoView('audit-content');
      }
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [scrollInTable, selected, isTablet, moveToSelected]);
};

const createBookmarkContents = (dataIntro, dataSections, dataContacts, onlySection) => ([
  {
    title: 'tableOfContents',
    data: dataIntro?.concat(dataSections).concat(dataContacts)
  },
  !onlySection && {
    title: 'unansweredQuestions',
    data: getUnansweredQuestions(dataSections),
    bookmarkId: 'question-view',
    divider: true,
  },
  !onlySection && {
    title: 'failedQuestions',
    data: getFailedQuestions(dataSections),
    bookmarkId: 'question-view',
    divider: true,
  },
]);

BookmarkProvider.propTypes = {
  scrollInTable: PropTypes.bool,
};

export { BookmarkProvider, createBookmarkContents, useBookmarkContext, useMoveSelectedBookmark };

